import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type { EvaluationCriterion } from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

/** Evaluation sub-tab: criteria (pre-submission scored items) vs kpis (post-award performance measures) */
export type EvaluationTab = 'criteria' | 'kpis'

/** Document kinds that indicate KPI/performance measures (post-award) */
const KPI_DOCUMENT_KINDS = ['kpi_reporting']

interface EvaluationCriteriaState {
  // State
  criteria: EvaluationCriterion[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Filter state
  showSuperseded: boolean
  activeTab: EvaluationTab

  // Actions - Fetching
  fetchCriteria: (bidId: string) => Promise<void>

  // Actions - CRUD
  updateCriterion: (id: string, data: Partial<EvaluationCriterion>) => Promise<EvaluationCriterion>

  // Actions - Utility
  clearError: () => void
  reset: () => void
  setShowSuperseded: (show: boolean) => void
  setActiveTab: (tab: EvaluationTab) => void

  // Computed
  getActiveCriteria: () => EvaluationCriterion[]
  getEvaluationCriteria: () => EvaluationCriterion[]
  getKpiCriteria: () => EvaluationCriterion[]
  getTabFilteredCriteria: () => EvaluationCriterion[]
  getCriteriaByParent: (parentId: string | null) => EvaluationCriterion[]
  getRootCriteria: () => EvaluationCriterion[]
  getCriterionById: (id: string) => EvaluationCriterion | undefined
  getTotalWeighting: () => number
  getCriteriaTree: () => CriterionWithChildren[]
  hasKpiCriteria: () => boolean
}

interface CriterionWithChildren extends EvaluationCriterion {
  children: CriterionWithChildren[]
}

export const useEvaluationCriteriaStore = create<EvaluationCriteriaState>()(
  devtools(
    (set, get) => ({
      // Initial state
      criteria: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      showSuperseded: false,
      activeTab: 'criteria' as EvaluationTab,

      // Fetch criteria for a bid
      fetchCriteria: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null })
          const criteria = await api.get<EvaluationCriterion[]>(`/bids/${bidId}/evaluation-criteria`)
          set({ criteria, isLoading: false, currentBidId: bidId })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch evaluation criteria',
            isLoading: false,
            currentBidId: bidId,
          })
        }
      },

      // Update a criterion
      updateCriterion: async (id: string, data: Partial<EvaluationCriterion>) => {
        try {
          set({ error: null })
          const criterion = await api.patch<EvaluationCriterion>(`/evaluation-criteria/${id}`, data)
          set((state) => ({
            criteria: state.criteria.map((c) => (c.id === id ? criterion : c)),
          }))
          return criterion
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update criterion',
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          criteria: [],
          isLoading: false,
          error: null,
          currentBidId: null,
          showSuperseded: false,
          activeTab: 'criteria' as EvaluationTab,
        })
      },

      // Toggle showing superseded items
      setShowSuperseded: (show: boolean) => {
        set({ showSuperseded: show })
      },

      // Set evaluation sub-tab
      setActiveTab: (tab: EvaluationTab) => {
        set({ activeTab: tab })
      },

      // Get active (non-superseded) criteria
      getActiveCriteria: () => {
        const { criteria, showSuperseded } = get()
        if (showSuperseded) return criteria
        return criteria.filter((c) => c.artifactStatus === 'active')
      },

      // Get evaluation criteria (non-KPI, pre-submission scored items)
      getEvaluationCriteria: () => {
        return get().getActiveCriteria().filter(
          (c) => !KPI_DOCUMENT_KINDS.includes(c.sourceDocumentKind ?? '')
        )
      },

      // Get KPI/performance measure criteria (from KPI reporting documents)
      getKpiCriteria: () => {
        return get().getActiveCriteria().filter(
          (c) => KPI_DOCUMENT_KINDS.includes(c.sourceDocumentKind ?? '')
        )
      },

      // Get criteria filtered by the active tab
      getTabFilteredCriteria: () => {
        const { activeTab } = get()
        return activeTab === 'kpis' ? get().getKpiCriteria() : get().getEvaluationCriteria()
      },

      // Check if any KPI criteria exist (for showing/hiding tab)
      hasKpiCriteria: () => {
        return get().getKpiCriteria().length > 0
      },

      // Get criteria by parent ID
      getCriteriaByParent: (parentId: string | null) => {
        const activeCriteria = get().getActiveCriteria()
        return activeCriteria.filter((c) => c.parentId === parentId)
      },

      // Get root criteria (no parent)
      getRootCriteria: () => {
        return get().getCriteriaByParent(null)
      },

      // Get criterion by ID
      getCriterionById: (id: string) => {
        return get().criteria.find((c) => c.id === id)
      },

      // Get total weighting (sum of root criteria percentages)
      getTotalWeighting: () => {
        const rootCriteria = get().getRootCriteria()
        return rootCriteria.reduce((sum, c) => {
          const percentage = c.weighting?.percentage ?? 0
          return sum + percentage
        }, 0)
      },

      // Build criteria tree structure
      getCriteriaTree: () => {
        const activeCriteria = get().getActiveCriteria()

        const buildTree = (parentId: string | null): CriterionWithChildren[] => {
          return activeCriteria
            .filter((c) => c.parentId === parentId)
            .map((c) => ({
              ...c,
              children: buildTree(c.id),
            }))
        }

        return buildTree(null)
      },
    }),
    { name: 'evaluation-criteria-store' }
  )
)
